import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Package, Plus, Upload, Download, Pencil, Trash2, Search, Wine, UtensilsCrossed, GlassWater
} from 'lucide-react';

const CATEGORY_CONFIG = {
  food: { label: 'Food', icon: UtensilsCrossed, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  beverage: { label: 'Beverage', icon: GlassWater, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  alcohol: { label: 'Alcohol', icon: Wine, color: 'bg-purple-50 text-purple-700 border-purple-200' },
};

export default function AdminInventory() {
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ item_name: '', category: 'food', unit: 'kg', rate: '', bottle_size_ml: '750' });
  const [saving, setSaving] = useState(false);
  const [alcoholEnabled, setAlcoholEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await axios.get(`${API}/outlets`, { headers: getHeaders() });
      setOutlets(res.data);
      if (res.data.length > 0) setSelectedOutlet(res.data[0].id);
    })();
  }, [API, getHeaders]);

  const loadItems = useCallback(async () => {
    if (!selectedOutlet) return;
    setLoading(true);
    try {
      const [itemsRes, configRes] = await Promise.all([
        axios.get(`${API}/admin/inventory/items?outlet_id=${selectedOutlet}`, { headers: getHeaders() }),
        axios.get(`${API}/admin/inventory/outlet-config?outlet_id=${selectedOutlet}`, { headers: getHeaders() }),
      ]);
      setItems(itemsRes.data);
      setAlcoholEnabled(configRes.data.alcohol_enabled);
    } catch { toast.error('Failed to load items'); }
    finally { setLoading(false); }
  }, [selectedOutlet, API, getHeaders]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleSave = async () => {
    if (!form.item_name.trim()) { toast.error('Item name is required'); return; }
    if (!form.rate || isNaN(form.rate)) { toast.error('Valid rate is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/inventory/items/${editItem.id}`, {
          ...form, rate: parseFloat(form.rate),
          ...(form.category === 'alcohol' ? { bottle_size_ml: parseInt(form.bottle_size_ml) || 750 } : {}),
        }, { headers: getHeaders() });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/admin/inventory/items`, {
          ...form, outlet_id: selectedOutlet, rate: parseFloat(form.rate),
          ...(form.category === 'alcohol' ? { bottle_size_ml: parseInt(form.bottle_size_ml) || 750 } : {}),
        }, { headers: getHeaders() });
        toast.success('Item added');
      }
      setAddOpen(false);
      setEditItem(null);
      setForm({ item_name: '', category: 'food', unit: 'kg', rate: '', bottle_size_ml: '750' });
      loadItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    try {
      await axios.delete(`${API}/admin/inventory/items/${item.id}`, { headers: getHeaders() });
      toast.success(`${item.item_name} deactivated`);
      loadItems();
    } catch { toast.error('Failed'); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ item_name: item.item_name, category: item.category, unit: item.unit, rate: String(item.rate), bottle_size_ml: String(item.bottle_size_ml || 750) });
    setAddOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/inventory/upload?outlet_id=${selectedOutlet}`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Created: ${res.data.created}, Updated: ${res.data.updated}${res.data.errors?.length ? `, Errors: ${res.data.errors.length}` : ''}`);
      if (res.data.errors?.length) res.data.errors.forEach(e => toast.error(e));
      loadItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory/template`, {
        headers: getHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleAlcoholToggle = async (val) => {
    try {
      await axios.put(`${API}/admin/inventory/outlet-config`, { outlet_id: selectedOutlet, alcohol_enabled: val }, { headers: getHeaders() });
      setAlcoholEnabled(val);
      toast.success(val ? 'Alcohol module enabled' : 'Alcohol module disabled');
    } catch { toast.error('Failed'); }
  };

  const filtered = items.filter(i => {
    if (i.status === 'inactive') return false;
    if (categoryFilter !== 'all' && i.category !== categoryFilter) return false;
    if (search && !i.item_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = { food: 0, beverage: 0, alcohol: 0 };
  items.filter(i => i.status !== 'inactive').forEach(i => { counts[i.category] = (counts[i.category] || 0) + 1; });

  return (
    <div className="space-y-4" data-testid="admin-inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A2421]">Inventory — Item Master</h1>
          <p className="text-sm text-[#5C6B64] mt-0.5">Manage items, rates, and categories per outlet</p>
        </div>
      </div>

      {/* Outlet selector + actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
          <SelectTrigger className="w-[220px] bg-white border-[#E2E8DF]" data-testid="outlet-select">
            <SelectValue placeholder="Select outlet" />
          </SelectTrigger>
          <SelectContent>
            {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-[#E2E8DF]">
            <Wine className="w-3.5 h-3.5 text-purple-600" />
            <span className="text-xs text-[#5C6B64]">Alcohol</span>
            <Switch checked={alcoholEnabled} onCheckedChange={handleAlcoholToggle} className="scale-75" data-testid="alcohol-toggle" />
          </div>
          <Button variant="outline" size="sm" className="border-[#E2E8DF] text-[#5C6B64] text-xs" data-testid="download-template"
            onClick={handleDownloadTemplate}>
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <Button variant="outline" size="sm" className="border-[#E2E8DF] text-[#5C6B64] text-xs relative" data-testid="upload-excel">
            <Upload className="w-3.5 h-3.5 mr-1" /> Upload
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
          </Button>
          <Button size="sm" onClick={() => { setEditItem(null); setForm({ item_name: '', category: 'food', unit: 'kg', rate: '', bottle_size_ml: '750' }); setAddOpen(true); }}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-xs" data-testid="add-item-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
          </Button>
        </div>
      </div>

      {/* Stats & Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 bg-white border border-[#E2E8DF] rounded-md px-3 py-1.5 flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 text-[#5C6B64]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..."
            className="bg-transparent text-sm outline-none flex-1 text-[#1A2421]" data-testid="item-search" />
        </div>
        <div className="flex gap-1">
          {['all', 'food', 'beverage', 'alcohol'].map(cat => (
            <button key={cat} onClick={() => setCategoryFilter(cat)}
              className={`text-[11px] px-2.5 py-1 rounded-md transition-colors ${
                categoryFilter === cat ? 'bg-[color:var(--color-primary)] text-white' : 'bg-white border border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2]'
              }`} data-testid={`filter-${cat}`}>
              {cat === 'all' ? `All (${items.filter(i=>i.status!=='inactive').length})` : `${CATEGORY_CONFIG[cat]?.label} (${counts[cat]})`}
            </button>
          ))}
        </div>
      </div>

      {/* Items Table */}
      <Card className="border-[#E2E8DF]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[#5C6B64]" data-testid="empty-items">
              <Package className="w-10 h-10 mb-2 text-[#E2E8DF]" />
              <p className="text-sm">{items.length === 0 ? 'No items yet. Add items or upload Excel.' : 'No items match filters.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="items-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <th className="px-4 py-2.5 text-left font-medium">Item Name</th>
                    <th className="px-4 py-2.5 text-left font-medium">Category</th>
                    <th className="px-4 py-2.5 text-left font-medium">Unit</th>
                    <th className="px-4 py-2.5 text-right font-medium">Rate</th>
                    <th className="px-4 py-2.5 text-right font-medium">Bottle Size</th>
                    <th className="px-4 py-2.5 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, i) => {
                    const cfg = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.food;
                    return (
                      <tr key={item.id} className={`border-b border-[#E2E8DF]/60 ${i % 2 ? 'bg-[#F9F9F8]/30' : ''}`} data-testid={`item-row-${item.id}`}>
                        <td className="px-4 py-2.5 font-medium text-[#1A2421]">{item.item_name}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={`${cfg.color} text-[10px]`}>{cfg.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{item.unit}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-[#1A2421]">{item.rate?.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-[#5C6B64]">
                          {item.category === 'alcohol' ? `${item.bottle_size_ml || 750} ml` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#5C6B64] hover:text-[color:var(--color-primary)]"
                              onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#5C6B64] hover:text-red-500"
                              onClick={() => handleDelete(item)} data-testid={`delete-item-${item.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setEditItem(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              {editItem ? 'Update item details. Rate changes are versioned.' : 'Add a new inventory item.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-[#5C6B64]">Item Name *</Label>
              <Input value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
                className="mt-1 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#5C6B64]">Category *</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="mt-1 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="beverage">Beverage</SelectItem>
                    <SelectItem value="alcohol">Alcohol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#5C6B64]">Unit *</Label>
                <Select value={form.unit} onValueChange={v => setForm(f => ({ ...f, unit: v }))}>
                  <SelectTrigger className="mt-1 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="ltr">ltr</SelectItem>
                    <SelectItem value="pcs">pcs</SelectItem>
                    <SelectItem value="gm">gm</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="bottle">bottle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#5C6B64]">Rate ({form.category === 'alcohol' ? 'per bottle' : form.category === 'food' ? 'per kg' : 'per ltr'}) *</Label>
              <Input type="number" step="0.01" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))}
                className="mt-1 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-rate-input" />
            </div>
            {form.category === 'alcohol' && (
              <div>
                <Label className="text-xs text-[#5C6B64]">Bottle Size (ml) *</Label>
                <Input type="number" value={form.bottle_size_ml} onChange={e => setForm(f => ({ ...f, bottle_size_ml: e.target.value }))}
                  placeholder="750" className="mt-1 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-bottle-size" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); setEditItem(null); }} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="save-item-btn">
              {saving ? 'Saving...' : (editItem ? 'Update' : 'Add Item')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
